/*!
 * Parenting Optimize - Knowledge Base
 * File: parenting-knowledge.js
 * Version: 1.0.0
 *
 * Purpose:
 * The structured parenting knowledge layer.
 *
 * This file contains the "brain" that the decision engine reads:
 * - parent natural response logic
 * - child temperament logic
 * - family pattern logic
 * - situation knowledge
 * - recommendation libraries
 * - feedback learning rules
 *
 * Important:
 * This file should not render UI.
 * This file should not manage navigation.
 * This file should not directly touch the DOM.
 */

(function (window) {
  "use strict";

  const PO = (window.PO = window.PO || {});

  // ==========================================================================
  // 01. KNOWLEDGE NAMESPACE
  // ==========================================================================

  PO.Knowledge = {
    version: "1.0.0",

    meta: {
      name: "Parenting Optimize Knowledge Base",
      principle: "Complex parenting logic underneath. Simple guidance on the surface.",
      customerFacingRule: "Never expose methodology names. Translate all logic into practical parent language."
    },

    // ==========================================================================
    // 02. METHODOLOGY MAP
    // These are internal design inspirations only.
    // They should not appear in customer-facing copy unless intentionally used
    // in a separate religious / educational edition.
    // ==========================================================================

    methodologies: {
      naturalResponse: {
        internalName: "Vilna Gaon four-temperament inspired natural response engine",
        publicName: "Natural Parenting Response",
        role: "Predict how a parent tends to react under stress.",
        weight: 15,
        useFor: [
          "parent stress response",
          "anger / intensity tendency",
          "avoidance tendency",
          "over-talking tendency",
          "boundary collapse tendency",
          "rigidity tendency"
        ],
        neverShow: [
          "fire",
          "water",
          "earth",
          "air",
          "middos",
          "Vilna Gaon",
          "Torah source"
        ]
      },

      bigFive: {
        internalName: "Big Five Personality Model",
        publicName: "Parenting Habit Dimensions",
        role: "Borrow useful personality dimensions, translated into parenting behavior.",
        weight: 8,
        useFor: [
          "consistency",
          "flexibility",
          "emotional reactivity",
          "social energy",
          "cooperation style"
        ],
        neverShow: [
          "neuroticism",
          "agreeableness score",
          "conscientiousness percentile"
        ]
      },

      childTemperament: {
        internalName: "Thomas & Chess temperament research",
        publicName: "Child Temperament Profile",
        role: "Understand how the child naturally responds to change, frustration, routines, intensity, and stimulation.",
        weight: 20,
        useFor: [
          "persistence",
          "adaptability",
          "transition difficulty",
          "sensitivity",
          "activity level",
          "emotional intensity",
          "regularity",
          "distractibility"
        ]
      },

      familySystems: {
        internalName: "Bowen family systems theory",
        publicName: "Family Pattern Engine",
        role: "Understand the home pattern rather than blaming one child or one parent.",
        weight: 20,
        useFor: [
          "parent alignment",
          "repeat cycles",
          "rescue loops",
          "split signals",
          "family stress",
          "who carries the emotional load",
          "who enforces",
          "who softens"
        ],
        neverShow: [
          "triangulation",
          "differentiation of self",
          "Bowen"
        ]
      },

      attachment: {
        internalName: "Attachment theory",
        publicName: "Connection Needs",
        role: "Determine when connection must come before correction.",
        weight: 8,
        useFor: [
          "separation sensitivity",
          "correction sensitivity",
          "reassurance needs",
          "repair after conflict"
        ]
      },

      adler: {
        internalName: "Alfred Adler",
        publicName: "Belonging + Contribution Engine",
        role: "Identify how the child can feel important, useful, and meaningfully included.",
        weight: 12,
        useFor: [
          "belonging",
          "importance",
          "family role",
          "contribution",
          "responsibility",
          "purpose"
        ]
      },

      dreikurs: {
        internalName: "Rudolf Dreikurs",
        publicName: "Behavior Purpose Engine",
        role: "Ask what the child may be trying to achieve through the behavior.",
        weight: 8,
        useFor: [
          "attention seeking",
          "power struggle",
          "revenge / hurt response",
          "avoidance of failure",
          "discouragement"
        ]
      },

      rossGreene: {
        internalName: "Ross Greene collaborative problem solving",
        publicName: "Skill Gap Engine",
        role: "Ask what skill may be missing rather than only what consequence is missing.",
        weight: 12,
        useFor: [
          "frustration tolerance",
          "transition skill",
          "problem solving",
          "emotional regulation",
          "language for needs",
          "waiting",
          "flexibility"
        ],
        guidingLine: "Children do well when they can."
      },

      siegel: {
        internalName: "Daniel Siegel / developmental neuroscience",
        publicName: "Brain State Engine",
        role: "Determine whether this is a teaching moment or a calming moment.",
        weight: 6,
        useFor: [
          "meltdown",
          "emotional flooding",
          "repair",
          "name it to tame it",
          "connect before redirect"
        ]
      },

      nelsen: {
        internalName: "Jane Nelsen / Positive Discipline",
        publicName: "Family Climate Engine",
        role: "Balance kindness and firmness, encouragement, gratitude, and respectful correction.",
        weight: 8,
        useFor: [
          "kind and firm",
          "encouragement",
          "logical consequences",
          "family meetings",
          "positive talk",
          "gratitude",
          "repair"
        ]
      },

      haidt: {
        internalName: "Jonathan Haidt / modern childhood environment",
        publicName: "Modern Environment Engine",
        role: "Account for screens, independence, anxiety, social comparison, play, and resilience.",
        weight: 5,
        useFor: [
          "screens",
          "social media",
          "autonomy",
          "outdoor play",
          "resilience",
          "overprotection",
          "friend culture"
        ]
      },

      learningLoop: {
        internalName: "Parenting Optimize adaptive feedback engine",
        publicName: "Learning Loop",
        role: "Remember what works for this child and adjust future recommendations.",
        weight: 20,
        useFor: [
          "feedback",
          "strategy success",
          "strategy failure",
          "child-specific learning",
          "family-specific adaptation",
          "reminders"
        ]
      }
    },

    // ==========================================================================
    // 03. PARENT NATURAL RESPONSE ENGINE
    // Hidden framework. Customer sees only plain-language output.
    // ==========================================================================

    parentResponses: {
      firm: {
        label: "Directive Parent Response",
        strengths: [
          "clear leadership",
          "fast decisions",
          "strong boundaries",
          "protective instinct"
        ],
        underStress: [
          "may increase pressure quickly",
          "may sound sharper than intended",
          "may focus on compliance before connection"
        ],
        bestAdviceStyle: [
          "short scripts",
          "soft opening",
          "firm but low-volume boundary",
          "avoid adding intensity"
        ],
        scriptGuidance: {
          opening: "Start softer than feels natural.",
          boundary: "Keep the boundary firm without increasing volume.",
          repair: "After the moment passes, reconnect briefly."
        }
      },

      explain: {
        label: "Explaining Parent Response",
        strengths: [
          "thoughtful",
          "teaches values",
          "tries to help the child understand",
          "communicates deeply"
        ],
        underStress: [
          "may talk too much",
          "may turn instruction into debate",
          "may accidentally invite negotiation"
        ],
        bestAdviceStyle: [
          "one-sentence instructions",
          "explain later, not during resistance",
          "clear choices",
          "avoid lectures"
        ],
        scriptGuidance: {
          opening: "Use fewer words than feels natural.",
          boundary: "Give one reason, not five.",
          repair: "Teach after calm returns."
        }
      },

      repeat: {
        label: "Repeating Parent Response",
        strengths: [
          "patient at first",
          "tries to avoid conflict",
          "gives chances",
          "does not rush punishment"
        ],
        underStress: [
          "may train the child to wait for the angry voice",
          "may delay follow-through",
          "may become resentful after repeating"
        ],
        bestAdviceStyle: [
          "say it once clearly",
          "one reminder maximum",
          "small follow-through",
          "predictable consequence"
        ],
        scriptGuidance: {
          opening: "Say it clearly the first time.",
          boundary: "Do not restart the instruction after each delay.",
          repair: "Notice cooperation quickly."
        }
      },

      "give-in": {
        label: "Softening Parent Response",
        strengths: [
          "warm",
          "empathetic",
          "emotionally available",
          "cares about the child's feelings"
        ],
        underStress: [
          "may change the rule to reduce distress",
          "may struggle to tolerate crying",
          "may become inconsistent"
        ],
        bestAdviceStyle: [
          "warm boundary",
          "expect protest",
          "do not change the rule immediately",
          "praise recovery"
        ],
        scriptGuidance: {
          opening: "Lead with warmth.",
          boundary: "Hold the limit even while validating feelings.",
          repair: "Let your child know you can handle their big feelings."
        }
      },

      variable: {
        label: "Variable Parent Response",
        strengths: [
          "flexible",
          "can adapt to different situations",
          "often intuitive",
          "not rigid"
        ],
        underStress: [
          "rules may feel unpredictable",
          "child may test which version of the parent shows up",
          "follow-through may depend on energy level"
        ],
        bestAdviceStyle: [
          "simple house rules",
          "repeatable scripts",
          "low-energy routines",
          "pre-decided consequences"
        ],
        scriptGuidance: {
          opening: "Use the same phrase each time.",
          boundary: "Let the rule carry the moment, not your mood.",
          repair: "Consistency matters more than perfection."
        }
      }
    },

    // ==========================================================================
    // 04. CHILD TEMPERAMENT ENGINE
    // ==========================================================================

    childTemperamentDimensions: {
      persistence: {
        low: {
          meaning: "This child may shift away from difficult tasks quickly.",
          usefulStrategies: [
            "short tasks",
            "fast wins",
            "encouragement for effort",
            "visible progress"
          ]
        },
        high: {
          meaning: "This child may hold onto wants, ideas, or resistance for a long time.",
          usefulStrategies: [
            "limited choices",
            "avoid power struggles",
            "give time to shift",
            "stay calm and predictable"
          ]
        }
      },

      adaptability: {
        low: {
          meaning: "This child may struggle when plans change or when one activity ends.",
          usefulStrategies: [
            "warnings before transitions",
            "visual routines",
            "countdowns",
            "first tiny step"
          ]
        },
        high: {
          meaning: "This child may adapt more easily to change.",
          usefulStrategies: [
            "simple direct instructions",
            "quick transitions",
            "let them help choose next step"
          ]
        }
      },

      sensitivity: {
        low: {
          meaning: "This child may not notice subtle emotional cues or small environmental changes.",
          usefulStrategies: [
            "clear words",
            "specific feedback",
            "physical routine cues"
          ]
        },
        high: {
          meaning: "This child may react strongly to tone, criticism, noise, crowds, clothes, or pressure.",
          usefulStrategies: [
            "soft voice",
            "connection before correction",
            "reduce stimulation",
            "private correction"
          ]
        }
      },

      intensity: {
        low: {
          meaning: "This child may show feelings quietly, even when upset.",
          usefulStrategies: [
            "check in gently",
            "watch body language",
            "invite words"
          ]
        },
        high: {
          meaning: "This child may feel and express emotion strongly.",
          usefulStrategies: [
            "calm first",
            "few words",
            "physical space",
            "teach after the storm"
          ]
        }
      },

      regularity: {
        low: {
          meaning: "This child may not naturally fall into predictable rhythms.",
          usefulStrategies: [
            "external structure",
            "visual schedule",
            "same order each day",
            "reduce decision points"
          ]
        },
        high: {
          meaning: "This child may do best when routines stay predictable.",
          usefulStrategies: [
            "protect routines",
            "prepare for exceptions",
            "give advance notice"
          ]
        }
      },

      activity: {
        low: {
          meaning: "This child may prefer quieter or slower-paced routines.",
          usefulStrategies: [
            "gentle transitions",
            "avoid rushing",
            "build in quiet reset time"
          ]
        },
        high: {
          meaning: "This child may need movement and physical outlet.",
          usefulStrategies: [
            "movement before seated tasks",
            "active responsibilities",
            "short bursts",
            "hands-on jobs"
          ]
        }
      }
    },

    childStrengthProfiles: {
      creative: {
        label: "Creative Contributor",
        whatHelps: [
          "choice",
          "imagination",
          "visual tools",
          "creative family roles"
        ],
        contributionIdeas: [
          "Design the family routine chart.",
          "Create a family game for after dinner.",
          "Decorate the table for a family meal.",
          "Plan a rainy-day activity."
        ],
        praiseStyle: "Praise originality and effort, not just the final result."
      },

      technical: {
        label: "Problem-Solving Contributor",
        whatHelps: [
          "practical jobs",
          "tools",
          "systems",
          "real responsibility"
        ],
        contributionIdeas: [
          "Organize chargers and devices.",
          "Check batteries in household items.",
          "Help assemble or fix something safe.",
          "Create a simple checklist for a routine."
        ],
        praiseStyle: "Praise usefulness, persistence, and problem solving."
      },

      sensitive: {
        label: "Sensitive Connector",
        whatHelps: [
          "soft tone",
          "privacy",
          "reassurance",
          "repair after conflict"
        ],
        contributionIdeas: [
          "Make a kind note for someone.",
          "Help prepare something thoughtful.",
          "Be in charge of welcoming guests.",
          "Help a younger sibling with something gentle."
        ],
        praiseStyle: "Praise care, noticing, and emotional effort."
      },

      "strong-willed": {
        label: "Determined Leader",
        whatHelps: [
          "controlled leadership",
          "limited choices",
          "clear boundaries",
          "respectful tone"
        ],
        contributionIdeas: [
          "Lead cleanup for five minutes.",
          "Choose between two family activities.",
          "Be responsible for one part of the routine.",
          "Help decide the order of tasks."
        ],
        praiseStyle: "Praise leadership used responsibly."
      },

      funny: {
        label: "Joy Builder",
        whatHelps: [
          "humor",
          "connection",
          "playful transitions",
          "being noticed positively"
        ],
        contributionIdeas: [
          "Tell a joke at dinner.",
          "Create a funny family award.",
          "Make a boring task into a race.",
          "Lead a short family game."
        ],
        praiseStyle: "Praise using humor to lift the family, not distract from responsibility."
      },

      helpful: {
        label: "Helper",
        whatHelps: [
          "being trusted",
          "meaningful jobs",
          "gratitude",
          "specific appreciation"
        ],
        contributionIdeas: [
          "Prepare snacks.",
          "Help a younger sibling.",
          "Set the table.",
          "Be the family kindness captain."
        ],
        praiseStyle: "Praise reliability and kindness."
      },

      organized: {
        label: "Organizer",
        whatHelps: [
          "order",
          "predictability",
          "checklists",
          "ownership"
        ],
        contributionIdeas: [
          "Set up the morning checklist.",
          "Organize school supplies.",
          "Plan the order of bedtime steps.",
          "Create a family calendar reminder."
        ],
        praiseStyle: "Praise planning and follow-through."
      },

      energetic: {
        label: "Energy Mover",
        whatHelps: [
          "movement",
          "short tasks",
          "physical jobs",
          "active transitions"
        ],
        contributionIdeas: [
          "Carry laundry.",
          "Run a quick errand inside the house.",
          "Lead a two-minute movement break.",
          "Help bring bags in from the car."
        ],
        praiseStyle: "Praise channeling energy into helpful action."
      }
    },

    // ==========================================================================
    // 05. FAMILY PATTERN ENGINE
    // ==========================================================================

    familyStructures: {
      single: {
        label: "Single-Parent Load",
        likelyChallenges: [
          "less backup",
          "higher emotional load",
          "harder to stay consistent when tired"
        ],
        planBias: [
          "simple routines",
          "low-energy scripts",
          "fewer moving parts",
          "make follow-through easy"
        ],
        language: "The plan should be simple enough to use without another adult stepping in."
      },

      "two-together": {
        label: "Two-Parent Alignment",
        likelyChallenges: [
          "different parenting instincts",
          "one parent may enforce while the other softens",
          "child may learn who to ask"
        ],
        planBias: [
          "agree on the rule first",
          "same wording",
          "avoid correcting each other in front of the child",
          "repair privately"
        ],
        language: "The plan should reduce mixed signals and make the rule clear from both adults."
      },

      shared: {
        label: "Shared-Custody Consistency",
        likelyChallenges: [
          "different homes",
          "different rules",
          "transition stress",
          "child may compare households"
        ],
        planBias: [
          "control what happens in this home",
          "avoid criticizing the other home",
          "predictable house rules",
          "transition rituals"
        ],
        language: "The plan should focus on predictable routines in your home, even if other settings are different."
      },

      other: {
        label: "Complex Family Setup",
        likelyChallenges: [
          "multiple adults",
          "unclear authority",
          "inconsistent expectations",
          "emotional triangles"
        ],
        planBias: [
          "clarify who is responsible",
          "reduce public disagreement",
          "use simple rules",
          "avoid multiple adults negotiating at once"
        ],
        language: "The plan should reduce confusion by clarifying the rule and the responsible adult."
      }
    },

    familyPatterns: {
      escalationLoop: {
        label: "Escalation Loop",
        signs: [
          "parent repeats",
          "child delays",
          "parent gets louder",
          "child complies only after intensity rises"
        ],
        breakLoop: [
          "one calm instruction",
          "one reminder",
          "small follow-through",
          "repair later"
        ]
      },

      negotiationLoop: {
        label: "Negotiation Loop",
        signs: [
          "every instruction becomes discussion",
          "child asks why repeatedly",
          "parent over-explains",
          "task gets delayed"
        ],
        breakLoop: [
          "one reason",
          "two choices",
          "stop debating",
          "teach later"
        ]
      },

      rescueLoop: {
        label: "Rescue Loop",
        signs: [
          "child protests",
          "parent softens",
          "rule changes",
          "child learns distress changes boundaries"
        ],
        breakLoop: [
          "validate feeling",
          "hold rule",
          "expect protest",
          "praise recovery"
        ]
      },

      splitSignal: {
        label: "Split-Signal Pattern",
        signs: [
          "adults give different answers",
          "child asks the softer adult",
          "one parent undermines the other",
          "rules depend on who is present"
        ],
        breakLoop: [
          "align privately",
          "use same sentence",
          "do not debate in front of child",
          "repair parent unity"
        ]
      },

      exhaustionLoop: {
        label: "Exhaustion Loop",
        signs: [
          "parent knows what to do but is too tired",
          "rules collapse at the end of the day",
          "child pushes harder when parent has low energy"
        ],
        breakLoop: [
          "simplify routine",
          "lower the number of decisions",
          "use scripts",
          "choose one battle"
        ]
      }
    },

    // ==========================================================================
    // 06. BEHAVIOR PURPOSE ENGINE
    // ==========================================================================

    behaviorPurposes: {
      attention: {
        label: "Connection Seeking",
        childBelief: "I matter when I get a reaction.",
        parentMove: [
          "give attention before the behavior escalates",
          "avoid big emotional reactions",
          "notice small positive behaviors quickly"
        ],
        phrase: "I see you. I will help you after this next step."
      },

      power: {
        label: "Control Seeking",
        childBelief: "I belong when I am in control.",
        parentMove: [
          "offer limited choices",
          "avoid direct power struggles",
          "let the child have control over small acceptable details"
        ],
        phrase: "You can choose this way or that way. The rule stays the same."
      },

      revenge: {
        label: "Hurt Response",
        childBelief: "If I feel hurt, I will hurt back.",
        parentMove: [
          "do not retaliate",
          "name the hurt later",
          "repair relationship after calm"
        ],
        phrase: "I will not let us hurt each other. We will fix this when we are calmer."
      },

      avoidance: {
        label: "Avoiding Failure",
        childBelief: "If I do not try, I cannot fail.",
        parentMove: [
          "make the task smaller",
          "praise effort",
          "remove shame",
          "start together"
        ],
        phrase: "You do not have to finish all of it now. Start with one small part."
      }
    },

    // ==========================================================================
    // 07. SKILL GAP ENGINE
    // ==========================================================================

    skillGaps: {
      transition: {
        label: "Transition Skill",
        signs: [
          "struggles when one activity ends",
          "needs many reminders to shift",
          "gets upset when plans change"
        ],
        buildWith: [
          "warnings",
          "visual routine",
          "first small step",
          "practice during calm times"
        ]
      },

      frustrationTolerance: {
        label: "Frustration Tolerance",
        signs: [
          "melts down when something is hard",
          "gives up quickly",
          "becomes angry when corrected"
        ],
        buildWith: [
          "short hard tasks",
          "encourage effort",
          "model calm mistake recovery",
          "avoid rescuing too fast"
        ]
      },

      flexibility: {
        label: "Flexibility",
        signs: [
          "needs things done one way",
          "argues when plans change",
          "gets stuck on fairness"
        ],
        buildWith: [
          "preview changes",
          "practice Plan B",
          "offer limited choices",
          "praise flexible moments"
        ]
      },

      communication: {
        label: "Needs Communication",
        signs: [
          "acts out instead of explaining",
          "says rude things when upset",
          "cannot ask for help calmly"
        ],
        buildWith: [
          "short scripts",
          "feelings words",
          "practice requests",
          "repair conversations"
        ]
      },

      impulseControl: {
        label: "Impulse Control",
        signs: [
          "grabs",
          "interrupts",
          "hits before thinking",
          "runs ahead"
        ],
        buildWith: [
          "pause practice",
          "physical structure",
          "clear boundaries",
          "short immediate feedback"
        ]
      }
    },

    // ==========================================================================
    // 08. BRAIN STATE ENGINE
    // ==========================================================================

    brainStates: {
      calm: {
        label: "Teaching Time",
        parentMove: [
          "teach",
          "practice",
          "ask reflective questions",
          "make agreements"
        ],
        avoid: [
          "over-talking",
          "lecturing"
        ]
      },

      rising: {
        label: "Reduce Intensity",
        parentMove: [
          "lower voice",
          "reduce words",
          "give space",
          "use simple choices"
        ],
        avoid: [
          "debating",
          "threatening"
        ]
      },

      flooded: {
        label: "Calm First",
        parentMove: [
          "safety first",
          "few words",
          "calm body",
          "teach later"
        ],
        avoid: [
          "logic",
          "consequences added in anger",
          "long explanations"
        ]
      },

      recovery: {
        label: "Repair Window",
        parentMove: [
          "reconnect",
          "name what happened simply",
          "practice one better response"
        ],
        avoid: [
          "restarting the fight",
          "shaming"
        ]
      }
    },

    // ==========================================================================
    // 09. FAMILY CLIMATE ENGINE
    // ==========================================================================

    familyClimate: {
      encouragement: {
        principle: "Children usually do better when they feel noticed for effort, usefulness, and progress.",
        practicalMoves: [
          "notice one good thing out loud each day",
          "speak positively about the child to another adult where the child may hear",
          "thank the child for a specific contribution",
          "write down one thing that went well"
        ]
      },

      kindAndFirm: {
        principle: "The goal is not harshness or permissiveness. The goal is warmth with a clear limit.",
        script: "I understand you are upset. The answer is still no."
      },

      repair: {
        principle: "Repair teaches children that conflict does not break the relationship.",
        script: "That was a hard moment. I love you. Next time we will try it differently."
      },

      gratitude: {
        principle: "A home that notices good creates more emotional safety.",
        practicalMoves: [
          "family gratitude at dinner",
          "one positive note per child per week",
          "thank children for unseen effort",
          "notice progress, not perfection"
        ]
      }
    },

    // ==========================================================================
    // 10. MODERN ENVIRONMENT ENGINE
    // ==========================================================================

    modernEnvironment: {
      screens: {
        risks: [
          "hard stopping points",
          "dopamine contrast with boring tasks",
          "negotiation after time is up",
          "sleep disruption"
        ],
        moves: [
          "set rule before screen starts",
          "use visual timer",
          "no negotiating at cutoff",
          "transition to a physical activity"
        ]
      },

      independence: {
        risks: [
          "too little responsibility",
          "too few real-world jobs",
          "low confidence",
          "excessive adult rescue"
        ],
        moves: [
          "give real responsibilities",
          "allow safe struggle",
          "avoid doing everything for the child",
          "praise capability"
        ]
      },

      play: {
        risks: [
          "not enough movement",
          "not enough peer negotiation",
          "too much adult-managed time"
        ],
        moves: [
          "build in free play",
          "allow age-appropriate independence",
          "use physical movement before seated tasks"
        ]
      }
    },

    // ==========================================================================
    // 11. SITUATION LIBRARY
    // V1 has core situations. Later this grows to hundreds.
    // ==========================================================================

    situations: {
      bedtime: {
        label: "Bedtime",
        category: "routine",
        likelyDrivers: [
          "transition difficulty",
          "separation need",
          "overtired child",
          "too many negotiations",
          "screen cutoff friction"
        ],
        primarySkills: [
          "transition",
          "self-regulation",
          "routine following"
        ],
        defaultPlan: {
          steps: [
            "Start the routine before the child is fully exhausted.",
            "Use the same short sequence each night.",
            "Give one calm choice inside the routine."
          ],
          avoid: [
            "Do not reopen negotiations after the routine begins.",
            "Do not add new consequences while angry.",
            "Do not turn bedtime into a long discussion."
          ]
        },
        parentScript: {
          soft: "I know you want more time. It is bedtime now. You can choose pajamas first or teeth first.",
          firm: "Bedtime has started. Choose pajamas or teeth. I will help you begin.",
          repair: "That was hard. Tomorrow we will start a little earlier."
        }
      },

      "not-listening": {
        label: "Not Listening",
        category: "cooperation",
        likelyDrivers: [
          "repeat-resist loop",
          "unclear instruction",
          "too many words",
          "child waiting for intensity"
        ],
        primarySkills: [
          "following directions",
          "attention",
          "transition"
        ],
        defaultPlan: {
          steps: [
            "Get close before giving the instruction.",
            "Say the instruction once clearly.",
            "Follow through with one small action instead of repeating."
          ],
          avoid: [
            "Do not call instructions from across the house.",
            "Do not repeat seven times.",
            "Do not wait until you are angry to act."
          ]
        },
        parentScript: {
          soft: "I need your eyes for one second. It is time to put your shoes on.",
          firm: "Shoes now. You can do it yourself or I can help you start.",
          repair: "You listened faster that time. That helped the house move calmly."
        }
      },

      screens: {
        label: "Screen Time",
        category: "technology",
        likelyDrivers: [
          "hard stopping point",
          "high reward activity",
          "rule unclear before starting",
          "parent negotiates at cutoff"
        ],
        primarySkills: [
          "stopping",
          "waiting",
          "frustration tolerance"
        ],
        defaultPlan: {
          steps: [
            "State the screen rule before the screen begins.",
            "Use a visible timer or countdown.",
            "When time ends, move to the next activity without renegotiating."
          ],
          avoid: [
            "Do not create the rule after the child is already watching.",
            "Do not debate when time is up.",
            "Do not extend time because of protest."
          ]
        },
        parentScript: {
          soft: "You can watch for twenty minutes. When the timer rings, the screen goes away.",
          firm: "The timer rang. Screen is done. You can put it away or I can help.",
          repair: "Stopping is hard. You did it, and that matters."
        }
      },

      homework: {
        label: "Homework",
        category: "school",
        likelyDrivers: [
          "avoidance of failure",
          "overwhelm",
          "skill gap",
          "too large a task",
          "parent-child power struggle"
        ],
        primarySkills: [
          "starting",
          "planning",
          "frustration tolerance"
        ],
        defaultPlan: {
          steps: [
            "Shrink the task to the first tiny step.",
            "Stay nearby for the first two minutes.",
            "Praise effort before correcting quality."
          ],
          avoid: [
            "Do not begin with the entire assignment.",
            "Do not shame the child for struggling.",
            "Do not turn homework into a battle about character."
          ]
        },
        parentScript: {
          soft: "We are not doing all of it right now. Start with the first problem.",
          firm: "The first step is opening the book. I will sit here for two minutes.",
          repair: "Starting was hard, but you started. That is progress."
        }
      },

      morning: {
        label: "Morning Routine",
        category: "routine",
        likelyDrivers: [
          "transition difficulty",
          "too many decisions",
          "parent stress",
          "unclear order"
        ],
        primarySkills: [
          "routine",
          "sequencing",
          "time awareness"
        ],
        defaultPlan: {
          steps: [
            "Use the same order every morning.",
            "Reduce choices before school.",
            "Give one small job at a time."
          ],
          avoid: [
            "Do not give five instructions at once.",
            "Do not start the day with criticism.",
            "Do not change the routine every morning."
          ]
        },
        parentScript: {
          soft: "First clothes, then breakfast. I will check back in two minutes.",
          firm: "Clothes first. Breakfast after. Start now.",
          repair: "This morning moved better when we used the same order."
        }
      },

      siblings: {
        label: "Sibling Conflict",
        category: "relationship",
        likelyDrivers: [
          "competition for attention",
          "unfairness feeling",
          "poor repair skill",
          "one child feels unseen"
        ],
        primarySkills: [
          "repair",
          "sharing",
          "problem solving",
          "emotional language"
        ],
        defaultPlan: {
          steps: [
            "Separate briefly if emotions are high.",
            "Ask what each child wanted or needed.",
            "Give each child one repair action."
          ],
          avoid: [
            "Do not decide the whole case while everyone is yelling.",
            "Do not label one child as always the problem.",
            "Do not force fake apologies too early."
          ]
        },
        parentScript: {
          soft: "Pause. I am going to hear both of you when voices are calmer.",
          firm: "Hands are not for hurting. Separate now. We will fix it after.",
          repair: "Each person needs one step to repair what happened."
        }
      },

      arguing: {
        label: "Arguing",
        category: "communication",
        likelyDrivers: [
          "negotiation loop",
          "need for control",
          "parent over-explains",
          "child delays through debate"
        ],
        primarySkills: [
          "respectful disagreement",
          "accepting limits",
          "flexibility"
        ],
        defaultPlan: {
          steps: [
            "Give one short reason.",
            "Offer two acceptable choices.",
            "Stop answering repeated objections."
          ],
          avoid: [
            "Do not argue as if you are trying to win a court case.",
            "Do not keep adding new explanations.",
            "Do not reward arguing with endless attention."
          ]
        },
        parentScript: {
          soft: "I hear that you disagree. The choice is A or B.",
          firm: "I answered. I am not debating this now.",
          repair: "You may disagree respectfully. We can talk more later."
        }
      },

      meltdown: {
        label: "Meltdown",
        category: "emotion",
        likelyDrivers: [
          "emotional flooding",
          "hunger",
          "tiredness",
          "overstimulation",
          "skill gap"
        ],
        primarySkills: [
          "regulation",
          "recovery",
          "language for feelings"
        ],
        defaultPlan: {
          steps: [
            "Focus on safety and lowering intensity.",
            "Use very few words.",
            "Teach or discuss only after the child is calmer."
          ],
          avoid: [
            "Do not lecture during the peak.",
            "Do not demand insight while the child is flooded.",
            "Do not add big consequences while you are upset."
          ]
        },
        parentScript: {
          soft: "You are having a hard time. I am here. We are going to slow down.",
          firm: "I will not let you hurt anyone. I am moving this away.",
          repair: "That was a big feeling. Next time we will practice what to do first."
        }
      }
    },

    // ==========================================================================
    // 12. RECOMMENDATION MODIFIERS
    // These modify situation advice based on parent / child / family profile.
    // ==========================================================================

    modifiers: {
      parent: {
        directive: {
          add: [
            "Use a softer opening before the boundary.",
            "Keep your voice lower than your instinct wants."
          ],
          avoid: [
            "Do not make the moment bigger with extra intensity."
          ]
        },

        explainer: {
          add: [
            "Use fewer words than feels natural.",
            "Save the lesson for later."
          ],
          avoid: [
            "Do not answer every objection."
          ]
        },

        repeater: {
          add: [
            "Decide your follow-through before giving the instruction.",
            "Use one reminder maximum."
          ],
          avoid: [
            "Do not repeat until you become angry."
          ]
        },

        rescuer: {
          add: [
            "Validate the feeling without changing the rule.",
            "Expect protest and remain warm."
          ],
          avoid: [
            "Do not remove the boundary just because the child is upset."
          ]
        },

        inconsistent: {
          add: [
            "Use the same phrase each time.",
            "Choose one small rule you can actually enforce today."
          ],
          avoid: [
            "Do not create rules in the middle of the conflict."
          ]
        }
      },

      child: {
        highPersistence: {
          add: [
            "Offer limited choices, not open-ended negotiation.",
            "Give the child time to shift without chasing the argument."
          ],
          avoid: [
            "Do not enter a power contest."
          ]
        },

        highSensitivity: {
          add: [
            "Correct privately when possible.",
            "Use a softer tone and repair afterward."
          ],
          avoid: [
            "Do not shame or criticize in front of others."
          ]
        },

        transitionDifficulty: {
          add: [
            "Give a warning before the change.",
            "Make the first step very small."
          ],
          avoid: [
            "Do not surprise the child with abrupt transitions."
          ]
        },

        highEnergy: {
          add: [
            "Use movement before seated tasks.",
            "Give a physical job when possible."
          ],
          avoid: [
            "Do not expect long stillness before regulation."
          ]
        }
      },

      family: {
        singleParentLoad: {
          add: [
            "Keep the plan simple enough to use without backup.",
            "Choose the smallest consequence that you can calmly enforce."
          ],
          avoid: [
            "Do not choose a strategy that requires more energy than you have tonight."
          ]
        },

        twoParentAlignment: {
          add: [
            "Align with the other parent privately when possible.",
            "Use the same rule wording."
          ],
          avoid: [
            "Do not correct the other adult in front of the child."
          ]
        },

        sharedCustody: {
          add: [
            "Focus on predictable rules in your home.",
            "Avoid comparing homes during the conflict."
          ],
          avoid: [
            "Do not criticize the other home in front of the child."
          ]
        }
      }
    },

    // ==========================================================================
    // 13. FEEDBACK LEARNING RULES
    // ==========================================================================

    feedbackRules: {
      worked: {
        meaning: "Strategy appears helpful.",
        futureBias: [
          "repeat similar approach",
          "increase confidence",
          "offer same structure in related situations"
        ],
        parentMessage: "Good. We'll remember that this kind of approach may work for this child."
      },

      somewhat: {
        meaning: "Strategy partly helped.",
        futureBias: [
          "keep core approach",
          "simplify steps",
          "adjust intensity"
        ],
        parentMessage: "Helpful to know. Next time we may simplify the plan."
      },

      "did-not-work": {
        meaning: "Strategy did not help.",
        futureBias: [
          "try alternate approach",
          "check wrong driver",
          "ask one more context question"
        ],
        parentMessage: "That matters. We'll adjust instead of repeating the same plan."
      },

      worse: {
        meaning: "Strategy escalated the situation.",
        futureBias: [
          "avoid similar approach",
          "prioritize calming",
          "reduce direct pressure",
          "ask if child was flooded"
        ],
        parentMessage: "Important feedback. Next time we should slow the moment down first."
      },

      "not-tried": {
        meaning: "No outcome data.",
        futureBias: [
          "remind gently",
          "make plan easier to try",
          "reduce number of steps"
        ],
        parentMessage: "No problem. We'll keep the next plan simple."
      }
    },

    // ==========================================================================
    // 14. OUTPUT STYLE GUIDE
    // This keeps the product light, practical, and premium.
    // ==========================================================================

    outputStyle: {
      maxSteps: 3,
      maxAvoid: 3,
      maxWhySentences: 4,
      tone: [
        "calm",
        "non-judgmental",
        "clear",
        "short",
        "practical",
        "warm"
      ],
      neverSay: [
        "your child is bad",
        "you caused this",
        "diagnosis",
        "treatment",
        "disorder",
        "therapy",
        "guaranteed",
        "cure"
      ],
      preferredPhrases: [
        "may be",
        "often",
        "try",
        "for now",
        "tonight",
        "this pattern",
        "what may be happening",
        "one small step"
      ],
      responseTemplate: {
        sections: [
          "What may be happening",
          "Try this tonight",
          "Why this fits",
          "Avoid for now",
          "What to notice"
        ]
      }
    },

    // ==========================================================================
    // 15. PUBLIC GETTERS
    // Engine can call these safely.
    // ==========================================================================

    getSituation(key) {
      return this.situations[key] || this.situations["not-listening"];
    },

    getParentResponse(key) {
      return this.parentResponses[key] || this.parentResponses.variable;
    },

    getFamilyStructure(key) {
      return this.familyStructures[key] || this.familyStructures.other;
    },

    getChildStrength(key) {
      return this.childStrengthProfiles[key] || null;
    },

    getFeedbackRule(key) {
      return this.feedbackRules[key] || this.feedbackRules.somewhat;
    },

    listSituations() {
      return Object.keys(this.situations).map((key) => ({
        key,
        label: this.situations[key].label,
        category: this.situations[key].category
      }));
    },

    listMethodologyRoles() {
      return Object.keys(this.methodologies).map((key) => ({
        key,
        publicName: this.methodologies[key].publicName,
        role: this.methodologies[key].role,
        weight: this.methodologies[key].weight
      }));
    }
  };

})(window);
